import { Sizes } from "enums/sizes";
import { usePanel } from "hooks/usePanel";
import { useCallback, useMemo } from "react"
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../contexts/main-borrower-pricing/MainBorrowerPricingContext";
import { BorrowerLoanFormPanel } from "features/subscription-process/profiling-process/forms/borrower-loan";
import { MinimalBorrowerForm } from "features/subscription-process/profiling-process/forms/borrower-minimal/MinimalBorrowerFormComponent";
import { useBorrowerRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey";
import { ReferentialKey } from "api/referential";
import { useAppSelector } from "app/hooks";
import { BorrowerProjectDocumentDto, getBorrowerProjectDocuments } from "services/borrowerProjectDocumentsAPI";

export const useCommonPanelForm = () => {
  const { borrowerProjectId, borrowerId } = useMainBorrowerPricingState();
  const riskData = useBorrowerRiskData()
  const project = riskData?.project
  const { initBorrowerPricingProcessAsync } = useMainBorrowerPricingActions();
  const { openPanel } = usePanel()
  const insuranceProjectReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.InsuranceProject))

  // Personnes déjà rattachées au projet : on les exclut de la recherche d'assurés
  // (sinon on peut re-sélectionner/recréer quelqu'un déjà présent).
  const alreadyOnProjectIds = useMemo(
    () =>
      (riskData?.insureds ?? [])
        .map((insured) => insured.id)
        .filter((id): id is string => !!id),
    [riskData?.insureds]
  )

  const reload = useCallback(() => {
    if (borrowerProjectId && borrowerId)
      initBorrowerPricingProcessAsync({ borrowerProjectId, emprunteurId: borrowerId })
  }, [borrowerProjectId, initBorrowerPricingProcessAsync, borrowerId])

  const isRenego = useMemo(() => {
    const ref = insuranceProjectReferential?.find(
      (ref) => ref.id === project?.insuranceProjectId
    )
    return ref?.code === 'RENEG_BANQUE' || ref?.code === "RENEG_COURTIER"
  }, [insuranceProjectReferential, project?.insuranceProjectId])

  const onCreateLoan = useCallback(async () => {
    // Project documents are read before opening the panel: with at least one file, the panel is widened to show them beside the form.
    let documents: BorrowerProjectDocumentDto[] = []
    if (borrowerProjectId) {
      try {
        documents = (await getBorrowerProjectDocuments(borrowerProjectId)).documents ?? []
      } catch (error) {
        console.error('Error fetching project documents:', error)
      }
    }
    openPanel({
      title: `Prêt 1`,
      size: documents.some(d => d.storedFileId) ? Sizes["7xl"] : Sizes["2xl"],
    },
      <BorrowerLoanFormPanel
        onSuccess={reload}
        isRenego={isRenego}
        borrowerProjectId={borrowerProjectId ?? undefined}
        projectDocuments={documents}
      />
    )
  }, [openPanel, reload, isRenego, borrowerProjectId])

  const onCreateInsured = useCallback(() => {
    openPanel({
      title: 'Nouvel assuré',
      size: Sizes["2xl"],
    },
      <MinimalBorrowerForm
        borrowerProjectId={borrowerProjectId ?? undefined}
        excludeIds={alreadyOnProjectIds}
        onSuccess={(actorId) => {
          if (borrowerProjectId && borrowerId) {
            initBorrowerPricingProcessAsync({ borrowerProjectId, emprunteurId: borrowerId })
          }
        }}
      />
    )
  }, [openPanel, borrowerId, borrowerProjectId, initBorrowerPricingProcessAsync, alreadyOnProjectIds])

  return {
    onCreateLoan,
    onCreateInsured
  }
}
